import React, { Component } from 'react'
import { inject, observer } from 'mobx-react';
import { observable } from 'mobx';
import { Alert, Intent } from '@blueprintjs/core';
import Pagination from 'components/Pagination';
import Footer from 'components/Footer';
import Toast from 'helpers/toaster';
import AddEquipmentModal from 'components/Equipment/AddEquipmentModal';
import qs from 'qs';
import * as DataList from 'components/DataList';

// Column and card layouts, shared with the loading skeleton (pages/equipments).
export const EQUIPMENT_COLUMNS = [
  { area: 'equipment', label: 'Equipment', width: 'minmax(200px, 1.4fr)', bone: 'person' },
  { area: 'model', label: 'Make / model', width: 'minmax(140px, 1fr)' },
  { area: 'where', label: 'Equipment location', width: 'minmax(130px, 0.9fr)', bone: 'single', icon: 'map-marker' },
  { area: 'description', label: 'Description', width: 'minmax(180px, 1.4fr)', bone: 'single', icon: 'align-left' },
  { area: 'client', label: 'Client', width: 'minmax(180px, 1.3fr)', icon: 'person' },
  { area: 'actions', label: '', width: '64px', bone: 'none' },
];

export const EQUIPMENT_CARDS = {
  columns: 'minmax(0, 1fr) auto',
  areas: [
    'equipment actions',
    'model model',
    'where where',
    'description description',
    'client client',
  ],
};

export const EQUIPMENT_MIN_TABLE_WIDTH = 1080;

const EQUIPMENT_FILTERS = {
  client_id: 'client',
  location_id: 'client location',
};

const thumbStyle = {
  flex: 'none',
  width: 40,
  height: 40,
  borderRadius: 8,
  objectFit: 'cover',
  display: 'block',
};

@inject('store', 'clientStore', 'equipmentStore', 'viewStore') @observer
class EquipmentsTable extends Component {
  @observable isOpenAddEquipmentDialog = false;
  @observable isDeleteDialogVisible = false;
  @observable equipment;

  onPaginationChange(newPage) {
    let {
      viewStore: {
        equipmentSearch
      }
    } = this.props;
    this.props.viewStore.equipmentSearch = {
      ...equipmentSearch,
      page: newPage
    };
    this.props.viewStore.equipments();
  }

  handleDeleteCancel() {
    this.isDeleteDialogVisible = false;
  }

  handleDeleteConfirm() {
    this.isDeleteDialogVisible = false;
    this.props.equipmentStore.deleteEquipment(this.equipment)
      .then(this.onSuccessfulDeleteEquipment.bind(this))
      .catch(this.onFailDeleteEquipment.bind(this))
  }

  onSuccessfulDeleteEquipment(response) {
    Toast.show({ icon: 'thumbs-up', message: 'Equipment deleted successfully!', intent: Intent.SUCCESS })
    this.equipment = null;
    this.isDeleteDialogVisible = false;
  }

  onFailDeleteEquipment(error) {
    this.isDeleteDialogVisible = false;
    Toast.show({ icon: 'warning-sign', message: 'Unable to delete equipment!', intent: Intent.DANGER })
  }

  handleEquipmentClick(event, equipment) {
    switch (event.label) {
      case 'Edit':
        this.equipment = equipment;
        this.props.viewStore.equipmentShow({uuid: equipment.attributes.id})
        break;
      case 'Delete':
        this.isDeleteDialogVisible = true;
        this.equipment = equipment;
        break;
      default:
    }
  }

  handleClosEquipmentDailog() {
    this.props.store.isOpenAddEquipmentDialog = false;
  }

  onSuccessAddEquipment(equipment) {
    this.props.store.isOpenAddEquipmentDialog = false;
    this.onPaginationChange(1);
  }

  clearFilters() {
    const { viewStore } = this.props;
    viewStore.equipmentSearch = {
      ...viewStore.equipmentSearch,
      term: '*',
      where: { client_id: null, location_id: null },
      page: 1,
    };
    viewStore.bumpListFilters();
    viewStore.equipments();
  }

  renderModals() {
    const { store } = this.props;
    return (
      <React.Fragment>
        <AddEquipmentModal
          isOpen={store.isOpenAddEquipmentDialog}
          onClose={this.handleClosEquipmentDailog.bind(this)}
          onSuccess={this.onSuccessAddEquipment.bind(this)}
        />
      </React.Fragment>
    );
  }

  render() {
    let {
      viewStore,
      equipmentStore: {
        equipments: {
          objects,
          meta
        }
      }
    } = this.props;
    const search = viewStore.equipmentSearch;
    if (objects.length === 0) {
      const term = search.term && search.term !== '*' ? search.term : null;
      const filters = DataList.activeFilters(search.where, EQUIPMENT_FILTERS);
      return (
        <DataList.Empty
          noun="equipment"
          filters={filters}
          term={term}
          onClearFilters={this.clearFilters.bind(this)}
          message={filters.length === 0 && !term ? 'Equipment you add for your clients will show up here.' : undefined}
        />
      );
    }
    return (
      <React.Fragment>
        <Footer>
          <div className="bp3-navbar-group bp3-align-left">
            <Pagination
              value={meta.page}
              total={meta.total_pages}
              onChange={this.onPaginationChange.bind(this)}
            />
          </div>
        </Footer>
        <DataList.List
          columns={EQUIPMENT_COLUMNS}
          cards={EQUIPMENT_CARDS}
          minTableWidth={EQUIPMENT_MIN_TABLE_WIDTH}
          total={meta.total_records || objects.length}
          noun="equipment"
          note={DataList.sortNote(search.order)}
        >
          {objects.map((equipment) => {
            const attributes = equipment.attributes;
            const client = attributes.client || {};
            const image = attributes.images && attributes.images.length > 0
              ? attributes.images[0].asset.thumb.url
              : '/../equipment_default.jpg';
            const name = attributes.kind || attributes.serial_number || 'Equipment';
            const serial = attributes.serial_number ? `SN ${attributes.serial_number}` : 'No serial number';
            return (
              <DataList.Row key={equipment.id}>
                <DataList.Cell area="equipment">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                    <img src={image} alt="" style={thumbStyle} />
                    <DataList.Lines
                      primary={
                        <DataList.RowLink
                          href={`/dashboard/equipments/${attributes.id}/show?${qs.stringify(viewStore.equipmentShowSearch, { encode: false })}`}
                          label={`Equipment ${name}`}
                        >
                          {name}
                        </DataList.RowLink>
                      }
                      secondary={attributes.kind ? serial : null}
                    />
                  </div>
                </DataList.Cell>
                <DataList.Cell area="model">
                  {attributes.make || attributes.eq_model ? (
                    <DataList.Lines
                      primary={attributes.make || 'Unknown make'}
                      secondary={attributes.eq_model}
                      strong={false}
                    />
                  ) : (
                    <DataList.Muted>No make or model</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="where">
                  {attributes.eq_location ? (
                    <DataList.Lines primary={attributes.eq_location} strong={false} />
                  ) : (
                    <DataList.Muted>No location</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="description">
                  {attributes.description ? (
                    <DataList.Lines primary={attributes.description} strong={false} />
                  ) : (
                    <DataList.Muted>No description</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="client">
                  {client.name ? (
                    <DataList.Lines
                      primary={client.name}
                      href={client.id
                        ? `/dashboard/clients/${client.id}?${qs.stringify(viewStore.clientShowSearch, { encode: false })}`
                        : undefined}
                      secondary={attributes.location && attributes.location.full_address}
                    />
                  ) : (
                    <DataList.Muted>No client</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="actions">
                  <DataList.Actions
                    items={[
                      { icon: 'edit', label: 'Edit equipment', onClick: () => viewStore.equipmentShow({ uuid: attributes.id }) },
                      { icon: 'trash', label: 'Delete equipment', intent: 'danger', onClick: () => this.handleEquipmentClick({ label: 'Delete' }, equipment) },
                    ]}
                  />
                </DataList.Cell>
              </DataList.Row>
            );
          })}
        </DataList.List>
        <Footer>
          <div className="bp3-navbar-group bp3-align-left">
            <Pagination
              value={meta.page}
              total={meta.total_pages}
              onChange={this.onPaginationChange.bind(this)}
            />
          </div>
        </Footer>
        <Alert
          cancelButtonText="Cancel"
          confirmButtonText="Delete"
          icon="trash"
          intent={Intent.DANGER}
          isOpen={this.isDeleteDialogVisible}
          onCancel={this.handleDeleteCancel.bind(this)}
          onConfirm={this.handleDeleteConfirm.bind(this)}
        >
          <p>
            Warnning! Deleting this equipment will also delete this equipment from all work report etc. and will not be recoverable.
          </p>
          <p>
            Are you sure you want to do this?
          </p>
        </Alert>
        {this.renderModals()}
      </React.Fragment>
    )
  }

}

export default EquipmentsTable;
